import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { APIProvider, Map as GoogleMap, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import {
  plottableResults,
  resultsMapFrame,
  shouldShowResultsMap,
  type MapFrame,
} from "@majourneys/shared";
import { MAPS_API_KEY, MAPS_MAP_ID } from "@majourneys/discover";
import { Icon } from "@majourneys/icons";
import type { AiMatch } from "../api/ai.api";
import type { ChatLang } from "../hooks/use-ai-chat";
import { activityHrefString } from "../../activities/lib/activity-href";
import { PewterCard } from "../../../shared/ui/pewter-card";
import { SOFT, RADIUS } from "./soft-tokens";
import { NumberPin, setActivePin, useIsActivePin } from "./number-pin";
import { useMatchPreview } from "./match-preview";

const MAP_HEIGHT = 240;

/**
 * This map is a short letterbox, so it takes a much smaller floor than the
 * phone's and leans on MAX_ZOOM to stop a single pin filling the frame with
 * one doorstep. `fitBounds` fits the tighter of the two axes, and at 240px
 * tall that is always the height: with the phone's 0.04-degree floor, six
 * medina restaurants ended up bunched in the middle third of the panel.
 */
const MIN_SPAN_DEG = 0.004;
/** As close as the frame may get, so two neighbouring venues still show streets. */
const MAX_ZOOM = 16;

/**
 * The answer as a map: one numbered pin per result, pin 2 being card 2.
 *
 * The phone's twin is `apps/mobile/src/features/discover/components/chat/
 * chat-results-map.tsx`. The three judgements that decide whether this map
 * tells the truth — which rows can be pinned, whether there are enough of them
 * to earn a map, and how far to zoom out — are in `@majourneys/shared` so the
 * two surfaces cannot answer them differently. Only the rendering differs
 * (Google Maps here, react-native-maps there).
 *
 * The reference labels its pins with a Google rating; we do not do ratings
 * (owner, 2026-09-16), and the number earns its place by answering the
 * question a rating never did — which pin is which card.
 *
 * Option B of the three drawn for the owner on 2026-09-16 ("pewter panel"):
 * the result rail stays BELOW the map rather than docked over it. The
 * reference (ChatGPT) docks its rail across the bottom, which on a Marrakech
 * answer covers the medina — the half of the map with the venues in it.
 */
export function ChatResultsMap({
  matches,
  lang,
}: {
  matches: AiMatch[];
  /** Locale for the venue links behind the pins. */
  lang: ChatLang;
}) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  /**
   * Which pin has its name bubble open, by slug.
   *
   * A number alone says which card a pin is, but only if you already have the
   * cards in view; on a twelve-restaurant answer the map sat above the rail
   * with no way to ask a pin what it was (owner, 2026-09-17). Tapping a pin
   * names it, tapping the name opens the venue.
   */
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  const pinned = useMemo(() => plottableResults(matches), [matches]);
  const frame = useMemo(() => resultsMapFrame(matches, { minSpanDeg: MIN_SPAN_DEG }), [matches]);
  const show = useMemo(() => shouldShowResultsMap(matches), [matches]);

  // No key, no map. Better a chat with no map than a grey box with Google's
  // "this page can't load Google Maps correctly" watermark over it.
  if (!show || !frame || !MAPS_API_KEY) return null;

  const leadSlug = pinned[0]?.slug;
  const height = expanded ? MAP_HEIGHT * 2 : MAP_HEIGHT;
  const open = pinned.find((m) => m.slug === openSlug) ?? null;

  return (
    <PewterCard
      radius={RADIUS.card}
      fill={SOFT.surface}
      style={{ margin: "0 0 12px" }}
      innerStyle={{ position: "relative", height, overflow: "hidden" }}
      data-testid={CARD_TESTID}
    >
      <APIProvider apiKey={MAPS_API_KEY} region="MA" language={i18n.language.split("-")[0]}>
        <GoogleMap
          mapId={MAPS_MAP_ID}
          // Pinned, never inherited: an unset scheme loads light tiles under a
          // dark thread, or the reverse, for as long as the tiles take.
          colorScheme="LIGHT"
          defaultCenter={{ lat: frame.latitude, lng: frame.longitude }}
          defaultZoom={DEFAULT_ZOOM}
          /**
           * Drag pans, pinch and ctrl+wheel zoom, a plain wheel scrolls the
           * thread. "none" shipped first and left the map a picture: the pins
           * you wanted were often just off frame with no way to reach them.
           * "greedy" is the other extreme, where the wheel zooms the map
           * instead of scrolling the answer and traps the page.
           */
          gestureHandling="cooperative"
          id={MAP_INSTANCE_ID}
          disableDefaultUI
          clickableIcons={false}
          style={{ width: "100%", height: "100%" }}
          onClick={() => setOpenSlug(null)}
        >
          <FitToResults frame={frame} height={height} />
          {pinned.map((m, i) => (
            <ResultPin
              key={m.slug}
              match={m}
              index={i}
              lead={m.slug === leadSlug}
              onToggle={() => setOpenSlug((s) => (s === m.slug ? null : m.slug))}
            />
          ))}
          {/* The bubble is its own marker, not content inside the pin's: a
              click on marker content also fires the marker's own handler
              natively, so a bubble nested in the pin would close itself on
              the way to the link. */}
          {open ? (
            <AdvancedMarker
              key={`${open.slug}-bubble`}
              position={{ lat: open.latitude, lng: open.longitude }}
              zIndex={BUBBLE_Z}
            >
              <PinBubble match={open} lang={lang} onClose={() => setOpenSlug(null)} />
            </AdvancedMarker>
          ) : null}
        </GoogleMap>
        {/* Outside the map element, inside the provider: `useMap(id)` reaches
            the instance from anywhere under APIProvider, so the buttons are
            positioned against the card like Expand rather than layered inside
            Google's own container. */}
        <ZoomControls />
      </APIProvider>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={expandButton}
      >
        {expanded ? t("ai_concierge.map_collapse") : t("ai_concierge.map_expand")}
        {/* <Icon>, never a bare <i>: the Remix webfont is gone, so an
            `<i className="ri-...">` draws nothing at all. */}
        <Icon
          name={expanded ? "ri-collapse-diagonal-line" : "ri-expand-diagonal-line"}
          size={14}
        />
      </button>
    </PewterCard>
  );
}

/**
 * One numbered pin, tied to its card (chat kit C6): pointing at the pin lifts
 * the card with the same number, and pointing at the card lifts this pin.
 * The lift is the pin's own transform; the marker's zIndex rises with it so a
 * lifted pin is never drawn under a neighbour.
 */
function ResultPin({
  match,
  index,
  lead,
  onToggle,
}: {
  match: AiMatch & { latitude: number; longitude: number };
  index: number;
  lead: boolean;
  onToggle: () => void;
}) {
  const active = useIsActivePin(match.slug);
  return (
    <AdvancedMarker
      position={{ lat: match.latitude, lng: match.longitude }}
      title={match.title ?? match.slug}
      /**
       * Explicit, because an unset zIndex is not "below 2": Google then
       * stacks the marker by latitude, and a pin a few streets south of the
       * open one was drawn straight over its bubble.
       */
      zIndex={active ? PIN_Z + 1 : PIN_Z}
      onClick={onToggle}
    >
      <span
        data-testid={`map-pin-${match.slug}`}
        onMouseEnter={() => setActivePin(match.slug)}
        onMouseLeave={() => setActivePin(null)}
        style={{ display: "inline-flex" }}
      >
        <NumberPin index={index} lead={lead} active={active} />
      </span>
    </AdvancedMarker>
  );
}

/**
 * Plus and minus, because ctrl+wheel is not a thing anyone discovers.
 *
 * Google's own zoom control is switched off with the rest of `disableDefaultUI`
 * and stays off: its grey chrome sits badly on the pewter card, and it would
 * be the only Google-styled control on the surface. These two speak the same
 * language as the Expand pill in the opposite corner.
 *
 * Panning and zooming never re-fit the frame. `FitToResults` runs once per
 * frame and height, so a reader who zooms in on the medina keeps that view
 * until the answer itself changes.
 */
function ZoomControls() {
  const { t } = useTranslation();
  const map = useMap(MAP_INSTANCE_ID);

  const step = (by: number) => {
    if (!map) return;
    const z = map.getZoom();
    if (z == null) return;
    map.setZoom(z + by);
  };

  return (
    <div style={zoomStack}>
      {/* The labels are `discover.*` rather than a second copy under
          `ai_concierge.*`: the same two words in all seven locales already. */}
      <button
        type="button"
        onClick={() => step(1)}
        aria-label={t("discover.zoom_in")}
        style={{ ...zoomButton, borderBottom: `1px solid ${SOFT.controlEdge}` }}
      >
        <Icon name="ri-add-line" size={17} />
      </button>
      <button
        type="button"
        onClick={() => step(-1)}
        aria-label={t("discover.zoom_out")}
        style={zoomButton}
      >
        <Icon name="ri-subtract-line" size={17} />
      </button>
    </div>
  );
}

/**
 * The open pin's name, sitting above it, and a link to the venue.
 *
 * The bubble carries the name and the city only. No price, no rating, no
 * "book": the card below already says all of that, and a pin tapped on a
 * crowded medina frame is asking one question, which place is this.
 *
 * A plain left click opens the same preview the cards open, where a provider
 * is mounted; it stays a real anchor so cmd-click, middle-click and "copy
 * link address" keep working.
 *
 * It sits above its pin, and drops below it when there is no room above. On
 * the frame the map opens with there always is, because `fitBounds` keeps 44px
 * of padding at the top, but a reader who drags a pin to the top edge had the
 * name clipped by about 20px.
 */
function PinBubble({
  match,
  lang,
  onClose,
}: {
  match: AiMatch;
  lang: ChatLang;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const preview = useMatchPreview();
  const { ref, below } = useFlipWhenClipped();
  const title = match.title ?? match.slug;
  const href = activityHrefString({
    lang,
    citySlug: match.citySlug,
    categorySlug: match.categorySlug,
    segment: match.segment,
    slug: match.slug,
  });

  return (
    <div ref={ref} style={below ? { ...bubbleWrap, ...bubbleWrapBelow } : bubbleWrap}>
      <a
        href={href}
        onClick={(e) => {
          if (!preview) return;
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          preview.open(match, e.currentTarget.getBoundingClientRect());
        }}
        style={bubbleLink}
        aria-label={match.cityName ? `${title}, ${match.cityName}` : title}
      >
        <span style={bubbleTitle}>{title}</span>
        {match.cityName ? <span style={bubbleMeta}>{match.cityName}</span> : null}
      </a>
      <button
        type="button"
        onClick={onClose}
        aria-label={t("common.close", { defaultValue: "Close" })}
        style={bubbleClose}
      >
        <Icon name="ri-close-line" size={14} />
      </button>
    </div>
  );
}

/**
 * Drops the bubble below its pin when it would be cut off at the top.
 *
 * Measured rather than projected: Google places the marker, so asking the
 * rendered bubble where it ended up is both shorter and truer than working out
 * the pin's pixel position from the map's bounds. The card is the clip, so it
 * is the frame the measurement is against.
 *
 * One-way, and only ever one flip per open bubble: reading the position again
 * after flipping and flipping back on the new reading is how this kind of code
 * ends up oscillating.
 */
function useFlipWhenClipped() {
  const ref = useRef<HTMLDivElement>(null);
  const [below, setBelow] = useState(false);

  useLayoutEffect(() => {
    if (below) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // jsdom, and any read before layout, report a zero-height box.
    if (rect.height === 0) return;
    const frame = el.closest(`[data-testid="${CARD_TESTID}"]`)?.getBoundingClientRect();
    if (!frame) return;
    if (rect.top < frame.top + EDGE_GAP) setBelow(true);
  }, [below]);

  return { ref, below };
}

/** Room the bubble wants between itself and the card's top edge. */
const EDGE_GAP = 4;

/**
 * Frames the map on the answer's own pins.
 *
 * `fitBounds` rather than a computed zoom level, because a zoom level alone
 * cannot know how wide the map is. The arithmetic version of this shipped for
 * about an hour and framed a six-restaurant answer at city scale: the formula
 * `log2(360 / span)` is only correct when the viewport is one 256px tile, and
 * this map is closer to 640px, so every frame came out more than a zoom step
 * too far out with the pins bunched in the middle.
 *
 * The bounds come from the shared frame, not from the raw minimum and maximum,
 * so a single day-trip pin cannot drag the view out of the city.
 */
function FitToResults({ frame, height }: { frame: MapFrame; height: number }) {
  const map = useMap();
  /**
   * Fit once per frame and once per height, never on pan or zoom.
   *
   * `height` is in the key because Expand grows the panel and Google does not
   * notice: the revealed half stayed blank grey until something asked the map
   * to redraw, which is what re-fitting does.
   */
  const key = [
    frame.latitude,
    frame.longitude,
    frame.latitudeDelta,
    frame.longitudeDelta,
    height,
  ].join(",");
  const applied = useRef<string | null>(null);

  useEffect(() => {
    if (!map || applied.current === key) return;
    applied.current = key;

    map.fitBounds(
      {
        north: frame.latitude + frame.latitudeDelta / 2,
        south: frame.latitude - frame.latitudeDelta / 2,
        east: frame.longitude + frame.longitudeDelta / 2,
        west: frame.longitude - frame.longitudeDelta / 2,
      },
      // Room for the Expand button's corner, and a little air all round.
      { top: 44, right: 16, bottom: 16, left: 16 },
    );
    const z = map.getZoom();
    if (z != null && z > MAX_ZOOM) map.setZoom(MAX_ZOOM);
  }, [map, key, frame]);

  return null;
}

/** Only ever on screen for the frame before `fitBounds` runs. */
const DEFAULT_ZOOM = 13;

/** The card is the clip a bubble can be cut off by, and the frame it measures against. */
const CARD_TESTID = "chat-results-map";

/** How the zoom buttons find the map they belong to. */
const MAP_INSTANCE_ID = "chat-results-map";

/** Every pin sits on one layer, the open bubble sits above all of them. */
const PIN_Z = 1;
const BUBBLE_Z = 100;

/** Stacked in the corner Expand does not use. */
const zoomStack: React.CSSProperties = {
  position: "absolute",
  bottom: 12,
  insetInlineEnd: 12,
  display: "flex",
  flexDirection: "column",
  borderRadius: 10,
  overflow: "hidden",
  background: SOFT.surface,
  border: `1px solid ${SOFT.controlEdge}`,
  boxShadow: "0 6px 18px rgba(4,0,51,0.12)",
};

/** WCAG 2.5.8: 32x32, comfortably over the 24x24 floor. */
const zoomButton: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  background: "transparent",
  border: 0,
  padding: 0,
  color: SOFT.ink,
  fontSize: 15,
  cursor: "pointer",
};

/**
 * Marker content is anchored bottom-centre on the coordinate, so the wrapper
 * is lifted clear of the 26px pin that stays drawn underneath it.
 */
const bubbleWrap: React.CSSProperties = {
  display: "flex",
  alignItems: "stretch",
  gap: 2,
  transform: "translateY(-32px)",
  maxWidth: 220,
  background: SOFT.surface,
  border: `1px solid ${SOFT.controlEdge}`,
  borderRadius: RADIUS.pill,
  boxShadow: "0 8px 22px rgba(4,0,51,0.18)",
  overflow: "hidden",
};

/**
 * The same bubble hanging under its pin, for a pin too close to the top edge.
 *
 * The 100% moves it its own height down, so the offset is the gap below the
 * coordinate rather than a number that has to be kept in step with the
 * bubble's height.
 */
const bubbleWrapBelow: React.CSSProperties = {
  transform: "translateY(calc(100% + 6px))",
};

const bubbleLink: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: 1,
  minWidth: 0,
  minHeight: 32,
  padding: "5px 4px 5px 12px",
  color: SOFT.ink,
  textDecoration: "none",
};

/** Long venue names get a second line rather than a cut-off first one. */
const bubbleTitle: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 700,
  lineHeight: 1.2,
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
  overflow: "hidden",
  overflowWrap: "anywhere",
};

/** #5a5d77 is the body meta colour that passes AA on this surface. */
const bubbleMeta: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  lineHeight: 1.2,
  color: "#5a5d77",
  overflowWrap: "anywhere",
};

/** WCAG 2.5.8: 24x24 minimum hit area, on a 32px-tall bubble. */
const bubbleClose: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  alignSelf: "stretch",
  background: "transparent",
  border: 0,
  padding: 0,
  color: "#5a5d77",
  fontSize: 14,
  cursor: "pointer",
};

const expandButton: React.CSSProperties = {
  position: "absolute",
  top: 12,
  insetInlineEnd: 12,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  // WCAG 2.5.8: 24x24 minimum hit area.
  minHeight: 32,
  padding: "0 13px",
  borderRadius: RADIUS.pill,
  background: SOFT.surface,
  border: `1px solid ${SOFT.controlEdge}`,
  color: SOFT.ink,
  fontSize: 12.5,
  fontWeight: 600,
  cursor: "pointer",
};
